// webapp/src/lib/adminCheck.ts
//
// Pure admin-check helper extracted from AdminRoute (Phase 17 IN-08-02).
//
// Calls is_admin() RPC. If the RPC fails with a JWT-stale error (PGRST301
// or message containing "JWT"), attempts ONE supabase.auth.refreshSession()
// and retries is_admin() once. Any other error → denied.
//
// Pure-ish: takes a minimal AdminCheckSupabase shape so we can unit-test
// the logic without rendering AdminRoute or pulling @testing-library/react.

export type AdminCheckResult = 'allowed' | 'denied'

export interface AdminCheckSupabase {
  // Supabase rpc() returns a PostgrestFilterBuilder which is *thenable* but
  // not a true Promise. We accept any thenable that resolves to {data,error}
  // so both the real client and a vi.fn() mock fit the contract.
  rpc: (fn: string) => PromiseLike<{ data: unknown; error: unknown }>
  auth: {
    refreshSession: () => PromiseLike<{ error: unknown }>
  }
}

function isJwtStaleError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { code?: string; message?: string }
  if (e.code === 'PGRST301') return true
  if (typeof e.message === 'string' && e.message.includes('JWT')) return true
  return false
}

/**
 * Run is_admin() with one JWT-refresh retry on stale-token errors.
 * Returns 'allowed' iff RPC ultimately yields { data: true, error: null }.
 *
 * @param sb Supabase client (minimal shape — rpc + auth.refreshSession).
 */
export async function checkIsAdminWithRefresh(
  sb: AdminCheckSupabase,
): Promise<AdminCheckResult> {
  const first = await sb.rpc('is_admin')
  if (!first.error) {
    return first.data ? 'allowed' : 'denied'
  }

  if (!isJwtStaleError(first.error)) {
    return 'denied'
  }

  // JWT-stale path: try ONE refresh + retry.
  const refresh = await sb.auth.refreshSession()
  if (refresh.error) {
    return 'denied'
  }

  const second = await sb.rpc('is_admin')
  if (second.error) return 'denied'
  return second.data ? 'allowed' : 'denied'
}
